import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  FileText,
  Users,
  Building,
  Clock,
  Target
} from 'lucide-react';

interface ComplianceMetric {
  id: string;
  name: string;
  status: 'compliant' | 'warning' | 'critical';
  dueDate: string;
  progress: number;
}

interface EntityMetric {
  id: string;
  name: string;
  type: 'corporation' | 'partnership' | 'individual';
  revenue: number;
  taxLiability: number;
  filingStatus: 'complete' | 'pending' | 'overdue';
}

const WealthOSDashboard: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState('2025');
  const [selectedEntity, setSelectedEntity] = useState('all');
  const [complianceMetrics, setComplianceMetrics] = useState<ComplianceMetric[]>([]);
  const [entityMetrics, setEntityMetrics] = useState<EntityMetric[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      
      if (typeof window !== 'undefined' && (window as any).electronAPI) {
        console.log('🖥️ WealthOS Desktop mode detected');
        document.title = 'WealthOS Desktop - Dashboard';
      }
      
      try {
        const apiBase = import.meta.env.MODE === 'development' ? 
          'http://localhost:3001' : 'http://localhost:3001';
        
        const dashboardEntity = selectedEntity === 'all' ? 'default' : selectedEntity;
        const response = await fetch(`${apiBase}/api/wealthos/dashboard/${dashboardEntity}/${selectedYear}`);
        const data = await response.json();
        
        if (data.success && data.data) {
          const complianceData = data.data.audit?.compliance || [];
          const entityData = data.data.bookkeeping?.entities || [];
          
          const mappedCompliance: ComplianceMetric[] = complianceData.map((item: any, index: number) => ({
            id: item.id || (index + 1).toString(),
            name: item.name || `Compliance Item ${index + 1}`,
            status: item.status || 'pending',
            dueDate: item.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            progress: item.progress || 0
          }));
          
          const mappedEntities: EntityMetric[] = entityData.map((item: any, index: number) => ({
            id: item.id || (index + 1).toString(),
            name: item.name || `Entity ${index + 1}`,
            type: item.type || 'corporation',
            revenue: item.revenue || 0,
            taxLiability: item.taxLiability || 0,
            filingStatus: item.filingStatus || 'pending'
          }));
          
          setComplianceMetrics(mappedCompliance.length > 0 ? mappedCompliance : getMockCompliance());
          setEntityMetrics(mappedEntities.length > 0 ? mappedEntities : getMockEntities());
          
          console.log('✅ WealthOS Dashboard data loaded from API');
        } else {
          throw new Error('API response was not successful');
        }
      } catch (error) {
        console.error('Failed to load dashboard data from API, using mock data:', error);
        
        setComplianceMetrics(getMockCompliance());
        setEntityMetrics(getMockEntities());
      }
      
      setLoading(false);
    };

    const getMockCompliance = (): ComplianceMetric[] => [
      {
        id: '1',
        name: 'Quarterly Tax Filing',
        status: 'warning',
        dueDate: '2025-04-15',
        progress: 75
      },
      {
        id: '2',
        name: 'Annual Compliance Review',
        status: 'compliant',
        dueDate: '2025-12-31',
        progress: 100
      },
      {
        id: '3',
        name: 'State Registration Renewal',
        status: 'critical',
        dueDate: '2025-03-01',
        progress: 25
      }
    ];

    const getMockEntities = (): EntityMetric[] => [
      {
        id: '1',
        name: 'TechCorp LLC',
        type: 'corporation',
        revenue: 2500000,
        taxLiability: 375000,
        filingStatus: 'complete'
      },
      {
        id: '2',
        name: 'Investment Partners',
        type: 'partnership',
        revenue: 1800000,
        taxLiability: 270000,
        filingStatus: 'pending'
      }
    ];

    loadDashboardData();
  }, [selectedYear, selectedEntity]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant':
      case 'complete':
        return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'warning':
      case 'pending':
        return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
      case 'critical':
      case 'overdue':
        return 'text-red-400 bg-red-500/20 border-red-500/30';
      default:
        return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading WealthOS Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-900 p-6 overflow-auto">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white text-drop-shadow mb-2">WealthOS Dashboard</h1>
            <p className="text-slate-400">Comprehensive tax and compliance overview</p>
          </div>
          
          <div className="flex items-center gap-4">
            <select
              value={selectedEntity}
              onChange={(e) => setSelectedEntity(e.target.value)}
              className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            >
              <option value="all">All Entities</option>
              <option value="techcorp">TechCorp LLC</option>
              <option value="investment">Investment Partners</option>
            </select>
            
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            >
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-teal-500/20 rounded-lg">
                <DollarSign className="w-6 h-6 text-teal-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-400">Total Revenue</h3>
                <p className="text-2xl font-bold text-white">{formatCurrency(4300000)}</p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <TrendingUp className="w-4 h-4 text-green-400 mr-1" />
              <span className="text-green-400">+12.5%</span>
              <span className="text-slate-400 ml-1">vs last year</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Target className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-400">Tax Liability</h3>
                <p className="text-2xl font-bold text-white">{formatCurrency(645000)}</p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <Clock className="w-4 h-4 text-yellow-400 mr-1" />
              <span className="text-yellow-400">Due in 45 days</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Building className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-400">Active Entities</h3>
                <p className="text-2xl font-bold text-white">{entityMetrics.length}</p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <Users className="w-4 h-4 text-blue-400 mr-1" />
              <span className="text-blue-400">2 Corporations, 1 Partnership</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-400">Compliance Score</h3>
                <p className="text-2xl font-bold text-white">87%</p>
              </div>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div className="bg-green-400 h-2 rounded-full" style={{ width: '87%' }}></div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Compliance Overview
            </h3>
            
            <div className="space-y-4">
              {complianceMetrics.map((metric) => (
                <div key={metric.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-white">{metric.name}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(metric.status)}`}>
                        {metric.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">Due: {metric.dueDate}</span>
                      <span className="text-slate-400">{metric.progress}% complete</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5 mt-2">
                      <div 
                        className="bg-teal-400 h-1.5 rounded-full transition-all duration-300" 
                        style={{ width: `${metric.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              Entity Status
            </h3>
            
            <div className="space-y-4">
              {entityMetrics.map((entity) => (
                <div key={entity.id} className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-white">{entity.name}</h4>
                      <p className="text-sm text-slate-400 capitalize">{entity.type}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(entity.filingStatus)}`}>
                      {entity.filingStatus}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-400">Revenue</span>
                      <p className="font-medium text-white">{formatCurrency(entity.revenue)}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Tax Liability</span>
                      <p className="font-medium text-white">{formatCurrency(entity.taxLiability)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default WealthOSDashboard;
